import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Heading } from '../../components/heading';
import { Text } from '../../components/text';
import {
  Table,
  TableHeader,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '../../components/table';
import TablePager from '../../components/TablePager';
import { ChevronRightIcon, ChevronDownIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import {
  aggregateProveedores,
  computeConvenioTotals,
  formatFechaCorta,
} from '../../utils/convenioCompliance';
import { ESTADO_FIANZA } from '../../utils/fianzaEventos';
import {
  ConvenioPicker,
  EmptyScope,
  StatTile,
  TODOS,
  fmtMXN,
  montoContrato,
  useConvenioScope,
} from './convenioScope';

const perPage = 8;

const pct = (n) => `${Math.round(n * 100)}%`;

/**
 * Cumplimiento por convenio — one row per convenio de proveedores: how much of
 * what the convenio requires is actually emitted, and what is overdue.
 *
 * The three numbers that matter are kept apart on purpose: `porEmitir` is a
 * requirement that can be issued today, `futura` is one that cannot (vicios
 * ocultos before the contract ends), and `atrasadas` is the subset of porEmitir
 * whose date already passed. Adding them together double-counts.
 *
 * Live compliance, not production: this report has no year filter, because a
 * requisito atrasado from a previous year is still atrasado today.
 */
export function CumplimientoConvenios() {
  const navigate = useNavigate();
  const location = useLocation();

  // Al saltar al convenio le dejamos en el state de dónde venimos, para que su
  // flecha de regreso vuelva al reporte (con ?reporte= ya puesto por Reportes)
  // y no al listado de convenios. `proveedor` preselecciona la tarjeta.
  const irAlConvenio = (convenioId, proveedorKey = null) => {
    const params = new URLSearchParams({ tab: 'proveedores' });
    if (proveedorKey) params.set('proveedor', proveedorKey);
    navigate(`/app/convenios/${convenioId}?${params.toString()}`, {
      state: { backTo: `${location.pathname}${location.search}` },
    });
  };
  const [convenioId, setConvenioId] = useState(TODOS);
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState(() => new Set());
  const { options, grupos, isAll, loading } = useConvenioScope(convenioId);

  useEffect(() => setPage(1), [convenioId]);

  const rows = useMemo(
    () =>
      grupos.map((g) => {
        const proveedores = aggregateProveedores(g.contracts, g.beneficiariosById, g.proyectosById);
        const totals = computeConvenioTotals(proveedores);
        const emitidasActivas = proveedores.reduce(
          (s, p) => s + p.emitidas.filter((e) => e._status.estado === ESTADO_FIANZA.ACTIVA).length,
          0
        );
        const exigibles = emitidasActivas + totals.fianzasPorEmitir;
        const atrasadas = proveedores.reduce((s, p) => s + p.counts.overdue, 0);
        const montoAtrasado = proveedores.reduce((s, p) => s + p.montos.overdue, 0);
        const oldestOverdue = proveedores.reduce((oldest, p) => {
          if (p.oldestOverdueDate == null) return oldest;
          return oldest == null || p.oldestOverdueDate < oldest ? p.oldestOverdueDate : oldest;
        }, null);
        return {
          ...g,
          proveedores,
          totals,
          emitidasActivas,
          atrasadas,
          montoAtrasado,
          oldestOverdue,
          contratado: g.contracts.reduce((s, c) => s + montoContrato(c), 0),
          cumplimiento: exigibles > 0 ? emitidasActivas / exigibles : null,
        };
      }),
    [grupos]
  );

  // Worst compliance first, then by exposure — the convenio that needs a call
  // today sits at the top.
  const sorted = useMemo(
    () =>
      [...rows].sort((a, b) => {
        if (a.montoAtrasado !== b.montoAtrasado) return b.montoAtrasado - a.montoAtrasado;
        if (a.atrasadas !== b.atrasadas) return b.atrasadas - a.atrasadas;
        return b.totals.montoPorEmitir - a.totals.montoPorEmitir;
      }),
    [rows]
  );

  const totales = useMemo(
    () =>
      rows.reduce(
        (acc, r) => ({
          convenios: acc.convenios + 1,
          proveedores: acc.proveedores + r.totals.totalProveedores,
          contratos: acc.contratos + r.totals.totalContratos,
          garantizado: acc.garantizado + r.totals.montoTotal - r.totals.montoPendiente,
          atrasadas: acc.atrasadas + r.atrasadas,
          montoAtrasado: acc.montoAtrasado + r.montoAtrasado,
          porEmitir: acc.porEmitir + r.totals.fianzasPorEmitir,
          montoPorEmitir: acc.montoPorEmitir + r.totals.montoPorEmitir,
          vencidas: acc.vencidas + r.totals.fianzasVencidas,
        }),
        {
          convenios: 0,
          proveedores: 0,
          contratos: 0,
          garantizado: 0,
          atrasadas: 0,
          montoAtrasado: 0,
          porEmitir: 0,
          montoPorEmitir: 0,
          vencidas: 0,
        }
      ),
    [rows]
  );

  // Picking one convenio means you want to see inside it.
  useEffect(() => {
    if (!isAll && grupos.length === 1) setExpanded(new Set([grupos[0].id]));
  }, [isAll, grupos]);

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const paged = sorted.slice((page - 1) * perPage, page * perPage);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <Heading level={2} className="text-xl text-azul-surelia">Cumplimiento por convenio</Heading>
          <Text className="text-sm text-azul-surelia/70">
            Qué exige cada convenio de proveedores y qué está realmente emitido, al día de hoy
          </Text>
        </div>
        <ConvenioPicker value={convenioId} onChange={setConvenioId} options={options} />
      </div>

      {loading ? (
        <div className="flex items-center justify-center min-h-[240px]">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-azul-surelia"></div>
        </div>
      ) : rows.length === 0 ? (
        <EmptyScope>Sin convenios de proveedores con contratos.</EmptyScope>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <StatTile label="Convenios" value={totales.convenios} sub={`${totales.proveedores} proveedores`} />
            <StatTile label="Contratos" value={totales.contratos} />
            <StatTile label="Garantizado" value={fmtMXN(totales.garantizado)} sub="fianzas emitidas vivas" />
            <StatTile
              label="Atrasadas"
              value={totales.atrasadas}
              sub={fmtMXN(totales.montoAtrasado)}
              alert={totales.atrasadas > 0}
            />
            <StatTile label="Por emitir" value={totales.porEmitir} sub={fmtMXN(totales.montoPorEmitir)} />
          </div>

          <div className="overflow-x-auto border border-azul-surelia/15 rounded-lg">
            <Table>
              <TableHead className="bg-azul-surelia text-white">
                <TableRow>
                  <TableHeader className="text-white w-8"></TableHeader>
                  <TableHeader className="text-white">Convenio</TableHeader>
                  <TableHeader className="text-white text-right">Proveedores</TableHeader>
                  <TableHeader className="text-white text-right">Contratos</TableHeader>
                  <TableHeader className="text-white text-right">Contratado</TableHeader>
                  <TableHeader className="text-white text-right">Garantizado</TableHeader>
                  <TableHeader className="text-white text-right">Cumplimiento</TableHeader>
                  <TableHeader className="text-white text-right">Atrasadas</TableHeader>
                  <TableHeader className="text-white text-right">Por emitir</TableHeader>
                  <TableHeader className="text-white text-right">Vencidas</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {paged.map((r) => {
                  const isOpen = expanded.has(r.id);
                  const garantizado = r.totals.montoTotal - r.totals.montoPendiente;
                  return (
                    <React.Fragment key={r.id}>
                      <TableRow className="cursor-pointer hover:bg-azul-surelia/5" onClick={() => toggle(r.id)}>
                        <TableCell className="py-3 pr-0">
                          {isOpen ? (
                            <ChevronDownIcon className="w-4 h-4 text-zinc-400" />
                          ) : (
                            <ChevronRightIcon className="w-4 h-4 text-zinc-400" />
                          )}
                        </TableCell>
                        <TableCell className="py-3">
                          <div className="flex items-center gap-2">
                            <Text className="text-sm font-medium text-azul-surelia">{r.nombre}</Text>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                irAlConvenio(r.id);
                              }}
                              className="text-zinc-400 hover:text-azul-surelia"
                              title="Abrir el convenio"
                            >
                              <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                            </button>
                          </div>
                          {r.oldestOverdue != null && (
                            <Text className="text-[11px] text-red-600">
                              Atraso más antiguo: {formatFechaCorta(r.oldestOverdue)}
                            </Text>
                          )}
                        </TableCell>
                        <TableCell className="py-3 text-right tabular-nums text-sm text-zinc-700">
                          {r.totals.totalProveedores}
                        </TableCell>
                        <TableCell className="py-3 text-right tabular-nums text-sm text-zinc-700">
                          {r.totals.totalContratos}
                        </TableCell>
                        <TableCell className="py-3 text-right tabular-nums text-sm text-zinc-700">
                          {fmtMXN(r.contratado)}
                        </TableCell>
                        <TableCell className="py-3 text-right tabular-nums text-sm font-semibold text-azul-surelia">
                          {fmtMXN(garantizado)}
                        </TableCell>
                        <TableCell className="py-3 text-right">
                          {r.cumplimiento == null ? (
                            <span className="text-sm text-zinc-400">-</span>
                          ) : (
                            <span
                              className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                                r.cumplimiento >= 1
                                  ? 'bg-green-100 text-green-800'
                                  : r.cumplimiento >= 0.8
                                  ? 'bg-amber-100 text-amber-800'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {pct(r.cumplimiento)}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="py-3 text-right">
                          <Text
                            className={`text-sm tabular-nums ${
                              r.atrasadas > 0 ? 'font-semibold text-red-600' : 'text-zinc-400'
                            }`}
                          >
                            {r.atrasadas || '-'}
                          </Text>
                        </TableCell>
                        <TableCell className="py-3 text-right tabular-nums text-sm text-zinc-700">
                          {r.totals.fianzasPorEmitir || '-'}
                          {r.totals.fianzasFuturas > 0 && (
                            <span className="text-zinc-400" title="Requisitos que sólo son exigibles al terminar el contrato">
                              {' '}
                              (+{r.totals.fianzasFuturas} fut.)
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="py-3 text-right tabular-nums text-sm text-zinc-700">
                          {r.totals.fianzasVencidas || '-'}
                        </TableCell>
                      </TableRow>
                      {isOpen && (
                        <TableRow className="bg-azul-surelia/5">
                          <TableCell colSpan={10} className="py-2 px-6">
                            <div className="sticky left-0 space-y-1">
                              {r.proveedores.length === 0 && (
                                <Text className="text-sm text-zinc-500">Sin proveedores registrados.</Text>
                              )}
                              {r.proveedores.map((p) => (
                                <button
                                  key={p.key}
                                  onClick={() => irAlConvenio(r.id, p.key)}
                                  className="w-full flex flex-wrap items-center gap-x-4 gap-y-1 text-left bg-white rounded-lg border border-zinc-200 px-3 py-1.5 hover:border-azul-surelia/40 transition-colors"
                                >
                                  <span className="text-sm text-zinc-800 min-w-[220px] truncate">{p.label}</span>
                                  <span className="text-xs text-zinc-500">
                                    {p.contracts.length} contrato{p.contracts.length !== 1 ? 's' : ''}
                                  </span>
                                  <span className="text-xs text-zinc-500">{p.counts.vigente} vigentes</span>
                                  {p.counts.overdue > 0 && (
                                    <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                                      {p.counts.overdue} atrasada{p.counts.overdue !== 1 ? 's' : ''}
                                    </span>
                                  )}
                                  {p.counts.porEmitir > 0 && (
                                    <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-800">
                                      {p.counts.porEmitir} por emitir
                                    </span>
                                  )}
                                  {p.counts.vencida > 0 && (
                                    <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-700">
                                      {p.counts.vencida} vencida{p.counts.vencida !== 1 ? 's' : ''}
                                    </span>
                                  )}
                                  <span className="text-xs text-zinc-600 tabular-nums ml-auto">
                                    {fmtMXN(p.montos.vigente)}
                                  </span>
                                </button>
                              ))}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <TablePager total={sorted.length} page={page} onPage={setPage} perPage={perPage} itemLabel="convenios" />
        </>
      )}
    </div>
  );
}
